import { useState } from 'react';
import { useFadeIn } from '@/hooks/useFadeIn';

export default function Schedule() {

  function LinkifyText({ text, linkColor = '#fc457e', linkHoverColor = '#1ef99d' }: { text: string; linkColor?: string; linkHoverColor?: string }) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const bracketRegex = /(【.*?】)/g;
    const parts = text.split(urlRegex);
    return (
      <>
        {parts.map((part, i) => {
          if (part.match(urlRegex)) {
            return (
              <a
                key={i}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="underline transition-colors"
                style={{ color: linkColor }}
                onMouseEnter={(e) => { (e.target as HTMLElement).style.color = linkHoverColor; }}
                onMouseLeave={(e) => { (e.target as HTMLElement).style.color = linkColor; }}
              >
                {part}
              </a>
            );
          }
          const bracketParts = part.split(bracketRegex);
          return bracketParts.map((bPart, j) => {
            if (bPart.match(bracketRegex)) {
              return <span key={`${i}-${j}`} className="text-[#fc457e] font-bold">{bPart}</span>;
            }
            const lines = bPart.split('\n');
            return (
              <span key={`${i}-${j}`}>
                {lines.map((line, k) => (
                  <span key={`${i}-${j}-${k}`}>
                    {k > 0 && <br />}
                    {line}
                  </span>
                ))}
              </span>
            );
          });
        })}
      </>
    );
  }

  const [openTicketIndexes, setOpenTicketIndexes] = useState<Set<number>>(new Set());
  const [openAdditionalTicketIndexes, setOpenAdditionalTicketIndexes] = useState<Set<number>>(new Set());
  const [openMainTicketIndexes, setOpenMainTicketIndexes] = useState<Set<number>>(new Set());
  const [openStreamingIndexes, setOpenStreamingIndexes] = useState<Set<number>>(new Set());
  const [openSummerFesIndexes, setOpenSummerFesIndexes] = useState<Set<number>>(new Set());
  const [apologyOpen, setApologyOpen] = useState(false);

  const toggleStreaming = (i: number) => {
    setOpenStreamingIndexes(prev => {
      const next = new Set(prev);
      if (next.has(i)) { next.delete(i); } else { next.add(i); }
      return next;
    });
  };

  const toggleSummerFes = (i: number) => {
    setOpenSummerFesIndexes(prev => {
      const next = new Set(prev);
      if (next.has(i)) { next.delete(i); } else { next.add(i); }
      return next;
    });
  };

  const toggleMainTicket = (i: number) => {
    setOpenMainTicketIndexes(prev => {
      const next = new Set(prev);
      if (next.has(i)) { next.delete(i); } else { next.add(i); }
      return next;
    });
  };

  const toggleTicket = (i: number) => {
    setOpenTicketIndexes(prev => {
      const next = new Set(prev);
      if (next.has(i)) { next.delete(i); } else { next.add(i); }
      return next;
    });
  };

  const toggleAdditionalTicket = (i: number) => {
    setOpenAdditionalTicketIndexes(prev => {
      const next = new Set(prev);
      if (next.has(i)) { next.delete(i); } else { next.add(i); }
      return next;
    });
  };

  // 小さなブロックごとのフェードインフック
  const blockMainTickets = useFadeIn(0);
  const blockHanedaTickets = useFadeIn(120);
  const blockStreaming = useFadeIn(240);
  const blockSummerFes = useFadeIn(360);
  const blockShow1 = useFadeIn(480);
  const blockShow2 = useFadeIn(540);
  const blockShow3 = useFadeIn(600);
  const blockShow4 = useFadeIn(660);
  const blockFooterNote = useFadeIn(720);

  const fadeClass = (hook: ReturnType<typeof useFadeIn>) =>
    `transition-all duration-[800ms] ease-out ${hook.isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-[0.97]'}`;

  const mainTicketSchedules = [
    {
      title: '一般販売（先着）',
      isNew: false,
      content: `販売時間：3月7日(土)10:00～\nチケット料金：\n■指定席：10,000円（税込）\n■自由席：9,000円（税込）\n※未就学児入場不可\n※お一人様１公演につき4枚まで\n※先着での受付です。予定枚数に達し次第受付を終了いたします。`
    },
    {
      title: '当日券（福岡公演）',
      isNew: true,
      content: `販売時間：2026/5/1(金)16:00〜\nチケット料金：\n■指定席：10,500円（税込）\n■自由席：9,500円（税込）\n※未就学児入場不可\n※お一人様１公演につき4枚まで\n※先着での受付です。予定枚数に達し次第受付を終了いたします。`
    }
  ];

  const ticketSchedules = [
    {
      title: '一般販売(先着)',
      items: [
        { label: '受付期間', value: '3月7日(土)10:00～' }
      ],
      itemsMobile: [
        { label: '受付期間', value: '3月7日(土)10:00～' }
      ]
    }
  ];

  const hanedaTicketSchedules = [
    {
      title: '一般発売(先着)',
      isNew: false,
      content: `受付期間：4/4(土)10:00～\n\n※VIPチケット、グッズ付き指定席、グッズ付きチケット(自由席)の販売は2026年5月20日(水)23:59まで`
    },
    {
      title: '当日券',
      isNew: false,
      content: `販売時間：2026/5/27(水)16:00〜\nチケット料金：\n■指定席：10,500円（税込）\n■自由席：9,500円（税込）\n※未就学児入場不可\n※お一人様１公演につき4枚まで\n※先着での受付です。予定枚数に達し次第受付を終了いたします。`
    }
  ];

  const shows = [
    {
      city: '東京公演',
      date: '2026年4月13日（月）',
      time: '17:00開場 / 18:00開演',
      venue: 'Kanadevia Hall',
      ticketUrls: [
        { label: 'ローソンチケット', url: 'https://l-tike.com/irinomiyu/' },
        { label: 'アニメイトチケット', url: 'https://animate-ticket.com/events/79' }
      ]
    },
    {
      city: '大阪公演',
      date: '2026年4月17日（金）',
      time: '17:00開場 / 18:00開演',
      venue: 'Zepp Osaka Bayside',
      ticketUrls: [
        { label: 'ローソンチケット', url: 'https://l-tike.com/irinomiyu/' },
        { label: 'アニメイトチケット', url: 'https://animate-ticket.com/events/79' }
      ]
    },
    {
      city: '福岡公演',
      date: '2026年5月1日（金）',
      time: '17:00開場 / 18:00開演',
      venue: 'Zepp Fukuoka',
      ticketUrls: [
        { label: 'ローソンチケット', url: 'https://l-tike.com/irinomiyu/' },
        { label: 'アニメイトチケット', url: 'https://animate-ticket.com/events/79' }
      ]
    },
    {
      city: '追加公演',
      date: '2026年5月27日（水）',
      time: '17:00開場 / 18:00開演',
      venue: 'Zepp Haneda',
      ticketUrls: [
        { label: 'ローソンチケット', url: 'https://l-tike.com/irinomiyu/' },
        { label: 'アニメイトチケット', url: 'https://animate-ticket.com/events/79' }
      ],
      isNew: true
    }
  ];

  const streamingSchedules = [
    {
      title: 'リアルタイム配信について',
      content: [
        '【リアルタイム配信】アフタートーク付き',
        '【配信日時】\n2026年5月27日(水)18：00開演',
        '【アーカイブ期間】\n2026年5月27日(水)配信終了後～\n5月28日(木)23:59',
        '※配信時間は状況により変更になる場合がございます。',
        '※視聴チケットとは別にユーザーチャージが発生します。',
        '※事前にご視聴方法のご案内がございますので、ライブ配信をご覧の方は余裕を持ってお買い求めください。直前でお買い求めの場合は、途中からの視聴になる場合がございます。',
        '※ライブ配信のチケットをお持ちのお客様は、アーカイブ配信も視聴可能です。',
        '※アーカイブ配信は期間中いつでもご視聴いただけます。',
        '【配信視聴チケット料金】\n視聴券　4,500円(税込)',
        '【チケット販売期間】\n2026年5月23日(土)15:00～\n5月28日(木)18:00',
        '【配信チケット】\nURL：https://l-tike.com/irinomiyu/',
        '※チケットのご購入・動画のご視聴には、ZAIKOアカウントへのご登録が必要となります。',
        '※配信内容は予告なく変更となる場合がございます。'
      ]
    },
    {
      title: '特典付き事後配信について',
      content: [
        '【特典付き事後配信】メイキング映像＋アフタートーク付き',
        '【配信日時】\n2026年5月30日(土)15:00～',
        '【アーカイブ期間】\n2026年5月30日(土)配信終了後～\n6月6日(土)23:59',
        '※リアルタイム配信から特典映像としてメイキング映像が付きます。',
        '※本編映像は場面により映像を編集しております。',
        '※アフタートークはリアルタイム配信と同じものになります。',
        '※配信時間は状況により変更になる場合がございます。',
        '※視聴チケットとは別にユーザーチャージが発生します。',
        '※事前にご視聴方法のご案内がございますので、ライブ配信をご覧の方は余裕を持ってお買い求めください。直前でお買い求めの場合は、途中からの視聴になる場合がございます。',
        '※ライブ配信のチケットをお持ちのお客様は、アーカイブ配信も視聴可能です。',
        '※アーカイブ配信は期間中いつでもご視聴いただけます。',
        '【配信視聴チケット料金】\n視聴券　4,500円(税込)',
        '【チケット販売期間】\n2026年5月27日(水)20:00～\n6月6日(土)18:00',
        '【配信チケット】\nURL：https://l-tike.com/irinomiyu/',
        '※チケットのご購入・動画のご視聴には、ZAIKOアカウントへのご登録が必要となります。',
        '※配信内容は予告なく変更となる場合がございます。',
        '※チケット販売画面は販売開始時間にオープン予定となります。'
      ]
    }
  ];

  const apologyContent = [
    '事後配信の販売開始日時につきまして、\n5月27日（火）20:00より販売開始とご案内しておりましたが、\n公開準備における不手際により、現在まで販売ページを公開できておりません。',
    '販売開始をお待ちいただいております皆様には、\n多大なるご迷惑とご心配をおかけしておりますこと、深くお詫び申し上げます。',
    '現在、販売ページの公開に向けて対応を進めております。\n販売ページは本日21:00より公開予定です。',
    'なお、事後配信の視聴開始日時である2026年5月30日（土）15:00からの配信につきましては、\n予定どおり実施いたします。視聴開始日時への影響はございませんので、ご安心ください。',
    'ご不便をおかけし誠に恐縮ではございますが、\n公開まで今しばらくお待ちいただけますと幸いです。\n何卒よろしくお願い申し上げます。'
  ];

  const summerFesSchedules = [
    {
      title: '入野自由 納涼祭 2026 開催決定',
      content: [
        '日程：2026年8月29日（土）',
        '会場：品川インターシティホール\n(〒108-0075 東京都港区港南2-15-4 品川インターシティ ホール棟)',
        '《1部》開場13:30／開演14:00',
        '《2部》開場16:00／開演16:30',
        '【チケット券種】',
        'グッズ付きチケット：10,500円(税込)[グッズ：アパレルステッカー付きポーチ]',
        '通常チケット：7,000円(税込)',
        '※「グッズ付きチケット」の販売はCD予約先行（CMS早期予約特典）のみでの販売となります。',
        '※ドリンク代別途必要',
        '※各券種は販売スケジュールに関係なく、販売予定数に達し次第終了致します。予めご了承ください。',
        '【CD予約先行（CMS早期予約特典）】',
        '受付期間(抽選)：2026年6月24日(水)15:00～7月5日(日)23:59まで',
        '入金受付期間：2026年7月10日(金)15:00～7月20日(月)23:00',
        '※シリアルコードを使用して、チケットのお申込みが可能です',
        'CD予約先行（CMS早期予約特典）はこちら\n\nhttps://shop.columbia.jp/shop/e/eHUMOR0829event/',
        '【オフィシャル先行（抽選）】',
        '受付期間：2026年7月23日(木)15:00～7月29日(水)23:59',
        '入金期間：2026年8月1日(土)15:00～8月5日(水)23:00',
        '『CMS限定セット』『アニメイト限定セット』の詳細はこちら\n\nhttps://columbia.jp/irinomiyu-uuu/summerfes.html'
      ]
    }
  ];

  const showBlocks = [blockShow1, blockShow2, blockShow3, blockShow4];

  return (
    <section
      id="schedule"
      className="py-20"
    >
      <div className="container mx-auto max-w-6xl px-4">
        <div className="text-center mb-16">
          <div className="inline-block bg-[#fcb122] px-8 md:px-12 py-3 md:py-4 shadow-[0_0_40px_#fcb122]">
            <h2 className="text-3xl md:text-5xl text-[#0a0a0a] font-bold">SCHEDULE</h2>
          </div>
        </div>

        {/* チケットスケジュール */}
        <div ref={blockMainTickets.ref} className={`bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-2 border-[#fcb122]/50 p-6 md:p-8 mb-8 ${fadeClass(blockMainTickets)}`}>
          <h3 className="text-xl md:text-2xl text-[#fcb122] font-bold mb-6 text-center">
            <span className="hidden md:inline">東京・大阪・福岡<br />チケットスケジュール</span>
            <span className="md:hidden">
              東京・大阪・福岡<br />チケットスケジュール
            </span>
          </h3>

          <div className="space-y-3">
            {mainTicketSchedules.map((s, i) => {
              const open = openMainTicketIndexes.has(i);
              return (
                <div key={i} className="border border-[#fcb122]/40 overflow-hidden">
                  <button
                    onClick={() => toggleMainTicket(i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3 pr-2">
                      <span className="text-sm md:text-base text-white font-bold">{s.title}</span>
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <span className={`text-[#fcb122] text-xl transition-transform duration-300 font-dotgothic ${open ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="px-4 pb-4">
                      <p className="text-xs md:text-sm text-white/90 whitespace-pre-line leading-relaxed">
                        {s.content}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 追加公演チケットスケジュール */}
        <div ref={blockHanedaTickets.ref} className={`bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-2 border-[#fcb122]/50 p-6 md:p-8 mb-8 ${fadeClass(blockHanedaTickets)}`}>
          <h3 className="text-xl md:text-2xl text-[#fcb122] font-bold mb-6 text-center">
            <span className="hidden md:inline">追加公演（Zepp Haneda）<br />チケットスケジュール</span>
            <span className="md:hidden">
              追加公演<br />（Zepp Haneda）<br />チケットスケジュール
            </span>
          </h3>

          <div className="space-y-3">
            {hanedaTicketSchedules.map((s, i) => {
              const open = openAdditionalTicketIndexes.has(i);
              return (
                <div key={i} className="border border-[#fcb122]/40 overflow-hidden">
                  <button
                    onClick={() => toggleAdditionalTicket(i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3 pr-2">
                      <span className="text-sm md:text-base text-white font-bold">{s.title}</span>
                      {s.isNew && (
                        <span className="bg-[#fc457e] text-white text-xs px-3 py-1 font-bold animate-pulse shadow-[0_0_10px_#fc457e]">
                          NEW
                        </span>
                      )}
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <span className={`text-[#fcb122] text-xl transition-transform duration-300 font-dotgothic ${open ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="px-4 pb-4">
                      <p className="text-xs md:text-sm text-white/90 whitespace-pre-line leading-relaxed">
                        {s.content}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 配信に関するご案内 */}
        <div ref={blockStreaming.ref} className={`bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-2 border-[#fc457e]/50 p-6 md:p-8 mb-8 ${fadeClass(blockStreaming)}`}>
          <h3 className="text-xl md:text-2xl text-[#fc457e] font-bold mb-6 text-center flex items-center justify-center gap-3">
            配信に関するご案内
          </h3>

          <div className="space-y-3">
            {streamingSchedules.map((s, i) => {
              const open = openStreamingIndexes.has(i);
              return (
                <div key={i} className="border border-[#fc457e]/40 overflow-hidden">
                  <button
                    onClick={() => toggleStreaming(i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3 pr-2">
                      <span className="text-sm md:text-base text-white font-bold">{s.title}</span>
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <span className={`text-[#fc457e] text-xl transition-transform duration-300 font-dotgothic ${open ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="px-4 pb-4 space-y-3">
                      {s.content.map((line, j) => (
                        <p
                          key={j}
                          className={`text-xs md:text-sm leading-relaxed whitespace-pre-line md:whitespace-normal ${
                            line.startsWith('※')
                              ? 'text-white/70'
                              : 'text-white'
                          }`}
                        >
                          <LinkifyText text={line} />
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 事後配信販売開始日時に関するお詫びとお知らせ */}
          <div className="mt-4 bg-gradient-to-r from-[#3a1a1a] to-[#2a1a1a] border-2 border-[#fc457e]/70 p-4 md:p-6 font-['Noto_Sans_JP']">
            <button
              onClick={() => setApologyOpen(!apologyOpen)}
              className="w-full flex items-center justify-between gap-4 cursor-pointer"
              aria-expanded={apologyOpen}
            >
              <div className="flex items-center gap-3">
                <span className="inline-block w-2 h-2 rounded-full bg-[#fc457e] animate-pulse shrink-0" />
                <h3 className="text-sm md:text-base text-[#fc457e] font-bold text-left leading-snug">
                  事後配信販売開始日時に関するお詫びとお知らせ
                </h3>
              </div>
              <span className="text-[#fc457e] shrink-0 text-2xl font-bold">
                {apologyOpen ? '−' : '+'}
              </span>
            </button>

            <div
              className={`transition-all duration-500 overflow-hidden ${apologyOpen ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`}
            >
              <div className="space-y-3 text-xs md:text-sm text-white/80 leading-relaxed">
                {apologyContent.map((line, j) => (
                  <p key={j} className="whitespace-pre-line">
                    {line}
                  </p>
                ))}
                <p className="text-right text-white/60 mt-2">
                  IRINO MIYU LIVE TOUR 2026 &quot;Humor is Human&quot; 配信運営事務局
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 入野自由 納涼祭2026 */}
        <div ref={blockSummerFes.ref} className={`bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-2 border-[#fcb122]/50 p-6 md:p-8 mb-8 ${fadeClass(blockSummerFes)}`}>
          <div className="space-y-3">
            {summerFesSchedules.map((s, i) => {
              const open = openSummerFesIndexes.has(i);
              return (
                <div key={i} className="overflow-hidden">
                  <button
                    onClick={() => toggleSummerFes(i)}
                    className="w-full flex items-center cursor-pointer hover:bg-white/5 transition-colors py-2"
                  >
                    <div className="flex-1 flex items-center justify-center gap-3 pr-2">
                      <h3 className="text-xl md:text-2xl text-[#fcb122] font-bold inline">
                        <span className="hidden md:inline">入野自由 納涼祭 2026 開催決定</span>
                        <span className="md:hidden">
                          入野自由 納涼祭2026<br />開催決定
                        </span>
                      </h3>
                      <span className="bg-[#fc457e] text-white text-xs px-3 py-1 font-bold animate-pulse shadow-[0_0_10px_#fc457e]">
                        NEW
                      </span>
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <span className={`text-[#fcb122] text-xl transition-transform duration-300 font-dotgothic ${open ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="space-y-3">
                      {s.content.map((line, j) => (
                        <p
                          key={j}
                          className={`text-xs md:text-sm leading-relaxed whitespace-pre-line md:whitespace-normal ${
                            line.startsWith('※')
                              ? 'text-white/70'
                              : 'text-white'
                          }`}
                        >
                          <LinkifyText text={line} linkColor="#44c4ff" linkHoverColor="#1ef99d" />
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 公演一覧 */}
        <div className="grid gap-6 md:gap-8">
          {shows.map((show, index) => (
            <div
              key={index}
              ref={showBlocks[index].ref}
              className={`block bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-4 border-[#44c4ff] p-6 md:p-8 transition-all duration-300 hover:border-[#1ef99d] hover:shadow-[0_0_40px_#44c4ff] group ${fadeClass(showBlocks[index])}`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-lg md:text-2xl text-[#44c4ff] group-hover:text-[#1ef99d] transition-colors font-dotgothic">
                      ▷
                    </span>
                    <h3 className="text-base md:text-xl font-bold text-[#44c4ff] group-hover:text-white transition-colors break-words">
                      {show.city}
                    </h3>
                  </div>

                  <div className="space-y-2 text-xs md:text-sm text-white/90">
                    <p className="flex items-center gap-2 break-words">
                      <i className="ri-calendar-line w-4 h-4 flex items-center justify-center text-[#44c4ff] shrink-0"></i>
                      <span className="break-words">{show.date}</span>
                    </p>
                    <p className="flex items-center gap-2 break-words">
                      <i className="ri-time-line w-4 h-4 flex items-center justify-center text-[#44c4ff] shrink-0"></i>
                      <span className="break-words">{show.time}</span>
                    </p>
                    <p className="flex items-center gap-2 break-words">
                      <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center text-[#44c4ff] shrink-0"></i>
                      <span className="break-words">{show.venue}</span>
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-center justify-center md:justify-end gap-3 shrink-0 md:w-48">
                  {show.ticketUrls.map((ticket, tIdx) => (
                    <a
                      key={tIdx}
                      href={ticket.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-[#44c4ff] text-[#0a0a0a] px-4 py-3 border-2 border-[#44c4ff] text-xs md:text-sm hover:bg-[#1ef99d] hover:border-[#1ef99d] hover:text-[#0a0a0a] transition-all whitespace-nowrap font-bold shadow-[0_0_15px_#44c4ff] hover:shadow-[0_0_20px_#1ef99d] font-press-start cursor-pointer text-center"
                    >
                      {ticket.label} <span className="font-dotgothic">▷</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div ref={blockFooterNote.ref} className={`mt-12 text-center ${fadeClass(blockFooterNote)}`}>
          <div className="inline-block bg-[#1a1a3e]/80 border-2 border-[#44c4ff]/50 px-6 py-4 max-w-full">
            <p className="text-xs md:text-sm text-white/70 leading-relaxed break-words">
              ※各公演の詳細・チケット購入は各リンクよりご確認ください。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}